import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, Smartphone, X, Trash2, Check, ArrowRight, Send, Calendar as CalendarIcon, Plus, Zap } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const AUDIENCES = [
  { id: "general", label: "General" },
  { id: "hombres", label: "Hombres" },
  { id: "mujeres", label: "Mujeres" },
  { id: "jovenes", label: "Jóvenes 18-25" },
  { id: "adultos", label: "Adultos 25-45" },
  { id: "fidelizados", label: "Fidelizados" },
];

const TONES = [
  { id: "agresivo_llamativo", label: "🔥 Agresivo / Llamativo" },
  { id: "emocional", label: "💔 Emocional" },
  { id: "comedia", label: "😂 Comedia mexicana" },
  { id: "intriga", label: "🤫 Intriga / Misterio" },
  { id: "urgencia", label: "⏰ Urgencia" },
];

const STATUS_COLORS = {
  programado: "pill-blue",
  enviado: "pill-green",
  cancelado: "pill-gray",
};

const PushMarketing = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/marketing/push").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const markSent = async (id) => {
    if (!window.confirm("¿Marcar como enviado? Aparecerá como publicado en el calendario.")) return;
    await api.post(`/marketing/push/${id}/mark-sent`);
    toast.success("Push marcado como enviado");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar push?")) return;
    await api.delete(`/marketing/push/${id}`);
    toast.success("Eliminado");
    load();
  };

  const grouped = items.reduce((acc, p) => {
    const s = p.status || "programado";
    (acc[s] = acc[s] || []).push(p);
    return acc;
  }, {});

  return (
    <Layout
      title="Push Marketing"
      subtitle="Notificaciones agresivas e imposibles de ignorar"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-push-btn">
          <Sparkles size={14} /> Generar push diarios
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <Smartphone size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin push programados</h3>
          <p className="text-[#666] mt-2 mb-5 max-w-md mx-auto">Indica el contexto del mes (promos, fechas clave) y Aura genera 5 push agresivos diferentes. Aprueba el que más te guste y se programa automáticamente al calendario editorial para Samantha.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Generar primer push</button>
        </div>
      ) : (
        <div className="space-y-6">
          {["programado", "enviado", "cancelado"].map((s) => grouped[s] && (
            <section key={s}>
              <h3 className="label-overline mb-3">{s} ({grouped[s].length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[s].map((p) => (
                  <PushCard key={p.id} item={p} onSent={() => markSent(p.id)} onDelete={() => remove(p.id)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {showForm && <PushGeneratorFlow onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </Layout>
  );
};

const PushCard = ({ item, onSent, onDelete }) => {
  return (
    <div className="aura-card p-4 group relative" data-testid={`push-${item.id}`}>
      {/* Push preview shape */}
      <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] text-white rounded-xl p-3 mb-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-5 h-5 rounded bg-[#D99D26] flex items-center justify-center text-[9px] font-bold">R</div>
          <span className="text-[10px] uppercase tracking-wider text-white/60">Rubí · ahora</span>
        </div>
        <div className="text-sm font-medium leading-tight">{item.title}</div>
        <div className="text-xs text-white/70 mt-1 line-clamp-2">{item.subtitle}</div>
      </div>
      <div className="text-xs text-[#666] space-y-1 mb-3">
        <div><span className="font-medium text-[#0A0A0A]">{item.target_date}</span> · {item.best_time || "—"}</div>
        <div className="flex gap-1 flex-wrap">
          <span className="pill pill-gray text-[10px]">{item.audience}</span>
          {item.product_target && <span className="pill pill-yellow text-[10px] truncate max-w-[120px]">{item.product_target}</span>}
        </div>
        {item.deep_link && <div className="text-[10px] text-[#8a5e10] truncate">→ {item.deep_link}</div>}
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-[#E5E5E5]">
        <span className={`pill ${STATUS_COLORS[item.status]}`}>{item.status}</span>
        <div className="flex gap-1">
          {item.status === "programado" && (
            <button onClick={onSent} className="text-xs text-[#8a5e10] font-medium hover:text-[#0A0A0A] flex items-center gap-1" data-testid={`mark-sent-${item.id}`}>
              <Send size={11} /> Marcar enviado
            </button>
          )}
          <button onClick={onDelete} className="text-[#666] hover:text-red-600 ml-2"><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
};

const PushGeneratorFlow = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    target_date: new Date().toISOString().slice(0, 10),
    month_context: "",
    products: "",
    promotions: "",
    audience: "general",
    tone: "agresivo_llamativo",
    extra_notes: "",
  });
  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/marketing/push/generate", {
        target_date: form.target_date,
        month_context: form.month_context,
        products: form.products.split(",").map((s) => s.trim()).filter(Boolean),
        promotions: form.promotions.split(",").map((s) => s.trim()).filter(Boolean),
        audience: form.audience,
        tone: form.tone,
        extra_notes: form.extra_notes,
        num_proposals: 5,
      });
      setProposals(res.data.proposals || []);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setGenerating(false); }
  };

  const pickProposal = (p) => {
    setEditing({ ...p });
    setStep(3);
  };

  const approve = async () => {
    setSaving(true);
    try {
      await api.post("/marketing/push/approve", {
        ...editing,
        target_date: form.target_date,
      });
      toast.success("Push aprobado · agregado al calendario y notificado a Samantha");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura · Push Marketing</div>
            <h2 className="font-display text-2xl font-medium">
              {step === 1 ? "Contexto del mes" : step === 2 ? "Elige tu push favorito" : "Edita y aprueba"}
            </h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#D99D26]" : "bg-[#F0F0F0]"}`} />)}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="aura-label">Fecha de envío *</label>
              <input type="date" className="aura-input" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="push-target-date" />
            </div>
            <div>
              <label className="aura-label">Contexto del mes (campañas, fechas clave) *</label>
              <textarea className="aura-input" rows="3" value={form.month_context} onChange={(e) => setForm({ ...form, month_context: e.target.value })} placeholder="Promo 4x3 todo el mes + Día del Padre + Mundial + 11 de Rubí..." data-testid="push-month-context" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="aura-label">Productos a destacar (separados por coma)</label>
                <input className="aura-input" value={form.products} onChange={(e) => setForm({ ...form, products: e.target.value })} placeholder="212 Men, Sauvage, perfume árabe X" />
              </div>
              <div>
                <label className="aura-label">Promociones vigentes</label>
                <input className="aura-input" value={form.promotions} onChange={(e) => setForm({ ...form, promotions: e.target.value })} placeholder="4x3, 11 de Rubí, 20% OFF Día Padre" />
              </div>
              <div>
                <label className="aura-label">Audiencia</label>
                <select className="aura-input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="push-audience">
                  {AUDIENCES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Tono</label>
                <select className="aura-input" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
                  {TONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="aura-label">Notas extras / ángulo deseado</label>
                <input className="aura-input" value={form.extra_notes} onChange={(e) => setForm({ ...form, extra_notes: e.target.value })} placeholder="Que sea descarado y polémico..." />
              </div>
            </div>
            <button onClick={generate} disabled={generating || !form.month_context.trim()} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="generate-push-btn">
              {generating ? <><span className="spinner" /> Aura escribiendo 5 push irresistibles...</> : <><Zap size={14} /> Generar 5 push</>}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-[#666]">Aura generó {proposals.length} propuestas. Click en la que más te guste para editarla y aprobarla.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {proposals.map((p, i) => (
                <div key={p.id} onClick={() => pickProposal(p)} className="aura-card p-4 cursor-pointer hover:border-[#D99D26] hover:shadow-md transition-all" data-testid={`push-proposal-${i}`}>
                  <div className="flex justify-between mb-2">
                    <span className="pill pill-yellow text-[10px]">Push {i + 1}</span>
                    <ArrowRight size={12} className="text-[#8a5e10]" />
                  </div>
                  <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] text-white rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 rounded bg-[#D99D26] flex items-center justify-center text-[8px] font-bold">R</div>
                      <span className="text-[9px] uppercase tracking-wider text-white/60">Rubí · ahora</span>
                    </div>
                    <div className="text-xs font-medium leading-tight">{p.title}</div>
                    <div className="text-[10px] text-white/70 mt-0.5 line-clamp-2">{p.subtitle}</div>
                  </div>
                  <div className="text-[10px] text-[#666]">
                    <div>{p.audience} · {p.best_time}</div>
                    {p.rationale && <div className="mt-1 italic line-clamp-2">💡 {p.rationale}</div>}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="btn-ghost">← Volver</button>
          </div>
        )}

        {step === 3 && editing && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="aura-label">Título (max 40 chars) — <span className="text-[#666]">{editing.title.length}/40</span></label>
                <input className="aura-input" maxLength={40} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="push-edit-title" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Subtítulo (max 90 chars) — <span className="text-[#666]">{editing.subtitle.length}/90</span></label>
                <textarea className="aura-input" maxLength={120} rows="2" value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="push-edit-subtitle" />
              </div>
              <div>
                <label className="aura-label">Producto/Promo destino</label>
                <input className="aura-input" value={editing.product_target} onChange={(e) => setEditing({ ...editing, product_target: e.target.value })} placeholder="212 Men 100ml" />
              </div>
              <div>
                <label className="aura-label">Deep link (Shopify/App)</label>
                <input className="aura-input" value={editing.deep_link} onChange={(e) => setEditing({ ...editing, deep_link: e.target.value })} placeholder="/producto/212-men o https://rubi.com/promo/4x3" />
              </div>
              <div>
                <label className="aura-label">Audiencia</label>
                <select className="aura-input" value={editing.audience} onChange={(e) => setEditing({ ...editing, audience: e.target.value })}>
                  {AUDIENCES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Mejor hora de envío</label>
                <input className="aura-input" value={editing.best_time} onChange={(e) => setEditing({ ...editing, best_time: e.target.value })} placeholder="19:30" />
              </div>
            </div>
            <div className="aura-card-flat p-4 bg-[#FFF9E6]/40 text-sm text-[#666]">
              <strong className="text-[#0A0A0A]">Al aprobar:</strong> el push se guarda como <em>programado</em>, se asigna a <strong>Samantha</strong> y aparece en el calendario editorial para el día seleccionado.
            </div>
            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(2)} className="btn-ghost">← Cambiar propuesta</button>
              <button onClick={approve} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="approve-push-btn">
                {saving ? <><span className="spinner" /> Guardando...</> : <><Check size={14} /> Aprobar y agregar al calendario</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushMarketing;
